import { useCallback, useEffect, useMemo, useRef } from 'react';

import { bind, type UnbindFn } from 'bind-event-listener';

const PRELOAD_MAX_AGE = 5 * 60 * 1000;
const TIME_TO_INTENT = 200;

export type OnLoad<T> = (obj: { dispose: () => void; reference: T }) => void;

export type UsePreloadRefProps<T> = {
	load: () => T;
	onLoad: OnLoad<T>;
};

export function usePreloadRef<T extends { dispose: () => void }>({
	load,
	onLoad,
}: UsePreloadRefProps<T>) {
	const nodeRef = useRef<HTMLElement | null>(null);
	const eventListeners = useRef<UnbindFn[]>([]);

	const { loadAndOpen, preloadReference, cancelPreload } = useMemo(() => {
		const request: {
			load?: { dispose: () => void; reference: T };
			preload?: { dispose: () => void; reference: T };
			preloadIntentTimeout?: ReturnType<typeof setTimeout>;
			preloadMaxAgeTimeout?: ReturnType<typeof setTimeout>;
		} = {};

		const clearPreloadTimeouts = () => {
			if (typeof request.preloadIntentTimeout === 'number') {
				clearTimeout(request.preloadIntentTimeout);
				delete request.preloadIntentTimeout;
			}

			if (typeof request.preloadMaxAgeTimeout === 'number') {
				clearTimeout(request.preloadMaxAgeTimeout);
				delete request.preloadMaxAgeTimeout;
			}
		};

		const cancelPreload = () => {
			clearPreloadTimeouts();
			request.preload?.dispose();
		};

		const loadReference = () => {
			if (request.load) {
				return request.load;
			}

			const reference = request.preload?.reference ?? load();
			if (request.preload) {
				delete request.preload;
			}

			request.load = {
				reference,
				dispose() {
					reference.dispose();
					delete request.load;
				},
			};

			return request.load;
		};

		const loadAndOpen = () => {
			clearPreloadTimeouts();
			onLoad(loadReference());
		};

		const preloadReference = () => {
			if (request.preloadIntentTimeout || request.preload) {
				return;
			}

			request.preloadIntentTimeout = setTimeout(() => {
				delete request.preloadIntentTimeout;

				const reference = load();

				request.preload = {
					reference,
					dispose() {
						reference.dispose();
						delete request.preload;
					},
				};

				request.preloadMaxAgeTimeout = setTimeout(() => {
					delete request.preloadMaxAgeTimeout;
					request.preload?.dispose();
				}, PRELOAD_MAX_AGE);
			}, TIME_TO_INTENT);
		};

		return {
			loadAndOpen,
			preloadReference,
			cancelPreload,
		};
	}, [load, onLoad]);

	const cleanUpEventListeners = useCallback(() => {
		eventListeners.current.forEach((unbind) => {
			unbind();
		});
		eventListeners.current = [];
	}, []);

	const addEventListener = useCallback(
		(element: HTMLElement, type: string, callback: EventListener) => {
			const unbind = bind(element, {
				type,
				listener: callback,
			});

			eventListeners.current.push(unbind);
		},
		[],
	);

	const refCallback = useCallback(
		(element: HTMLElement | null) => {
			if (element !== nodeRef.current) {
				if (nodeRef.current) {
					cancelPreload();
					cleanUpEventListeners();
				}
				if (element) {
					addEventListener(element, 'mouseenter', preloadReference);
					addEventListener(element, 'mouseleave', cancelPreload);
				}
				nodeRef.current = element;
			}
		},
		[preloadReference, cancelPreload, addEventListener, cleanUpEventListeners],
	);

	useEffect(() => {
		return () => {
			cleanUpEventListeners();
		};
	}, [cleanUpEventListeners]);

	return Object.assign(refCallback, {
		loadAndOpen,
	});
}
